/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartTitle
} from '@progress/kendo-react-charts';

const SUPPORTED_TYPES = ['pie', 'bar', 'line'];

const DEFAULT_COLORS = [
  '#4F46E5',
  '#06B6D4',
  '#22C55E',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#14B8A6',
  '#F97316',
  '#0EA5E9',
  '#A855F7'
];

const normalizePoints = (raw) => {
  const arr = Array.isArray(raw) ? raw : [];

  return arr
    .map((p, idx) => {
      if (p == null) return null;

      // Common shapes:
      // 1) { label, value, percentage }
      // 2) { category, value }
      // 3) { x, y }
      if (typeof p === 'object' && !Array.isArray(p)) {
        const category = p.category ?? p.label ?? p.x;
        const value = p.value ?? p.y;
        if (category == null || value == null) return null;
        return {
          category: String(category),
          value: Number(value),
          color: p.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]
        };
      }

      // 4) tuple [category, value]
      if (Array.isArray(p) && p.length >= 2) {
        return {
          category: String(p[0]),
          value: Number(p[1]),
          color: DEFAULT_COLORS[idx % DEFAULT_COLORS.length]
        };
      }

      return null;
    })
    .filter(Boolean);
};

// Parse backend timestamp formats into Date objects for time/category axis
// Supports:
// - "YYYYMMDDHHmmss" (14 digits)
// - epoch milliseconds (13 digits) / seconds (10 digits)
// - Date strings parsable by Date constructor
const parseTimestampLike = (x) => {
  if (x == null) return null;
  if (x instanceof Date && !isNaN(x)) return x;
  const s = String(x);
  // 14-digit: YYYYMMDDHHmmss (assume local time if UTC not required)
  if (/^\d{14}$/.test(s)) {
    const y = Number(s.slice(0, 4));
    const mo = Number(s.slice(4, 6));
    const d = Number(s.slice(6, 8));
    const hh = Number(s.slice(8, 10));
    const mm = Number(s.slice(10, 12));
    const ss = Number(s.slice(12, 14));
    return new Date(y, mo - 1, d, hh, mm, ss);
  }
  // epoch millis
  if (/^\d{13}$/.test(s)) return new Date(Number(s));
  // epoch seconds
  if (/^\d{10}$/.test(s)) return new Date(Number(s) * 1000);
  const d2 = new Date(s);
  return isNaN(d2) ? null : d2;
};

// A lightweight Kendo chart renderer for dashboard tiles.
// Supports only: pie, bar.
const SimpleKendoChart = ({ chartData, height = 240 }) => {
  const containerRef = useRef(null);
  const [chartKey, setChartKey] = useState('init');

  const inferredType = chartData?.lines?.[0]?.type || chartData?.Lines?.[0]?.Type;
  const type = String(chartData?.type || inferredType || '').toLowerCase();

  const supported = SUPPORTED_TYPES.includes(type);
  // Titles: prefer top-level; for line also fallback to first line.*
  const title =
    chartData?.title ||
    chartData?.Title ||
    chartData?.chart_title ||
    (type === 'line' ? (chartData?.lines?.[0]?.title || '') : '') ||
    '';
  const xAxisTitle =
    chartData?.x_axis ||
    chartData?.xAxis ||
    (type === 'line' ? (chartData?.lines?.[0]?.x_axis || chartData?.lines?.[0]?.xAxis) : '') ||
    '';
  const yAxisTitle =
    chartData?.y_axis ||
    chartData?.yAxis ||
    (type === 'line' ? (chartData?.lines?.[0]?.y_axis || chartData?.lines?.[0]?.yAxis) : '') ||
    '';

  const points = useMemo(
    () => normalizePoints(chartData?.data),
    [chartData]
  );

  const maxValue = useMemo(() => {
    if (type === 'line') {
      const lines = Array.isArray(chartData?.lines) ? chartData.lines : [];
      const vals = lines.flatMap((ln) =>
        (Array.isArray(ln?.data) ? ln.data : [])
          .map(p => Number(p?.y ?? p?.value))
          .filter(v => Number.isFinite(v))
      );
      return vals.length ? Math.max(...vals) : 0;
    }
    const vals = points.map(p => (Number.isFinite(p?.value) ? p.value : 0));
    return vals.length ? Math.max(...vals) : 0;
  }, [points, chartData, type]);

  const niceAxisMax = useMemo(() => {
    if (!maxValue || maxValue <= 0) return 10;
    // round up to a "nice" number
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
    const normalized = maxValue / magnitude;
    const rounded = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
    return rounded * magnitude;
  }, [maxValue]);

  const total = useMemo(
    () => points.reduce((sum, p) => sum + (Number.isFinite(p?.value) ? p.value : 0), 0),
    [points]
  );

  // Build multi-series for line charts
  const lineSeries = useMemo(() => {
    if (type !== 'line') return [];
    const rawLines = Array.isArray(chartData?.lines)
      ? chartData.lines
      : // fallback: single-series if chartData.data contains {x,y}
        (Array.isArray(chartData?.data) && chartData.data.some(p => p && typeof p === 'object' && 'x' in p && 'y' in p)
          ? [{ name: chartData?.name || 'Series 1', data: chartData.data }]
          : []);
    return rawLines.map((ln, idx) => ({
      name: ln?.name || `Series ${idx + 1}`,
      color: ln?.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length],
      data: (Array.isArray(ln?.data) ? ln.data : [])
        .map(p => {
          const rawX = p?.x ?? p?.category;
          const val = Number(p?.y ?? p?.value);
          // For line charts, we now support both time-like and categorical X values.
          // If X parses as a timestamp, we keep it as Date; otherwise we treat it as a string category.
          if (rawX == null || !Number.isFinite(val)) return null;
          const parsed = parseTimestampLike(rawX);
          const cat = parsed != null ? parsed : rawX;
          return { category: cat, value: val };
        })
        .filter(Boolean)
    }));
  }, [chartData, type]);

  // Detect whether any line-series category looks like a Date so we can decide
  // whether to use time-based axis formatting (baseUnit/format) or treat X as
  // plain categorical values.
  const isTimeLikeLine = useMemo(() => {
    if (type !== 'line') return false;
    return lineSeries.some(s => Array.isArray(s.data) && s.data.some(pt => pt?.category instanceof Date));
  }, [type, lineSeries]);

  if (!supported) {
    return (
      <div className="tile-loading-message">
        Unsupported chart type: {type || 'unknown'}
      </div>
    );
  }

  if (type !== 'line') {
    if (!points.length) {
      return (
        <div className="tile-loading-message">
          No chart data
        </div>
      );
    }
  } else {
    const hasAnyLinePoints = lineSeries.some(s => Array.isArray(s.data) && s.data.length > 0);
    if (!hasAnyLinePoints) {
      return (
        <div className="tile-loading-message">
          No chart data
        </div>
      );
    }
  }

  // Kendo types: 'pie' works as-is; render bar as vertical columns.
  const kendoType = type === 'bar' ? 'column' : type;

  const pieLabelContent = (props) => {
    const c = props?.dataItem?.category;
    const v = props?.dataItem?.value;
    const p = props?.dataItem?.percentage;

    // Prefer backend-provided percentage, otherwise compute if we can.
    const pct = Number.isFinite(p)
      ? p / 100
      : total > 0 && Number.isFinite(v)
        ? v / total
        : null;

    const formattedPct =
      pct == null
        ? ''
        : ` (${Number(pct).toLocaleString(undefined, {
            style: 'percent',
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
          })})`;

    return `${c}: ${v}${formattedPct}`;
  };

  const barLabelContent = (props) => {
    const v = props?.dataItem?.value;
    // Category is already visible on the X axis; keep the bar label as numeric value only.
    return `${v}`;
  };

  // Let the chart grow/shrink with tile size.
  // Use minHeight to keep pie readable.
  const minHeight = type === 'pie' ? 300 : Math.max(180, Number(height) || 240);

  const chartArea =
    type === 'pie'
      ? {
          // Reserve space for title + outside labels inside tight tiles
          margin: { top: 16, right: 8, bottom: 8, left: 8 }
        }
      : { margin: { top: 8, right: 12, bottom: 44, left: 52 } };
  const plotArea =
    type === 'pie'
      ? {
          // Keep the pie larger while leaving some breathing room for labels
          margin: { top: 8, right: 8, bottom: 8, left: 8 }
        }
      : { margin: 24 };

  // IMPORTANT:
  // KendoReact Charts calculate layout based on container size.
  // When TileLayout changes columns (large -> small screens), the chart can keep the old size
  // and its SVG can clip labels. In Edit mode, resizing triggers a reflow; we replicate that
  // by remounting the Chart on container resize.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    let raf = 0;
    const ro = new ResizeObserver(entries => {
      const cr = entries?.[0]?.contentRect;
      if (!cr) return;
      const w = Math.round(cr.width);
      const h = Math.round(cr.height);
      if (!w || !h) return;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setChartKey(`${w}x${h}`);
      });
    });

    ro.observe(el);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        minHeight,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div style={{ flex: '1 1 auto', minHeight: 0 }}>
        <Chart
          key={chartKey}
          style={{ width: '100%', height: '100%' }}
          // Extra padding so labels don’t get clipped in tight tile cards
          chartArea={chartArea}
          plotArea={plotArea}
          // Prevent legend clicks from hiding series/slices (users reported disappearing charts)
          onLegendItemClick={(e) => {
            try {
              e?.preventDefault?.();
              e?.syntheticEvent?.preventDefault?.();
            } catch (_) {}
          }}
          seriesDefaults={{ toggleable: false }}
        >
          {title ? <ChartTitle text={title} /> : null}
          {/* Legend: hide for pie (we render custom below), show for line so series names are visible */}
          <ChartLegend visible={type === 'line'} position="bottom" orientation="horizontal" />
          {type === 'bar' || type === 'line' ? (
            <>
              <ChartCategoryAxis>
                <ChartCategoryAxisItem
                  title={{
                    text: xAxisTitle || undefined,
                    font: '12px sans-serif',
                    color: '#111827'
                  }}
                  labels={
                    type === 'line'
                      ? (
                          isTimeLikeLine
                            ? { rotation: 'auto', visible: true, color: '#374151', format: '{0:MM/dd HH:mm}' }
                            : { rotation: 'auto', visible: true, color: '#374151' }
                        )
                      : { rotation: 'auto', visible: true, color: '#374151' }
                  }
                  baseUnit={type === 'line' && isTimeLikeLine ? 'fit' : undefined}
                  majorGridLines={{ visible: true }}
                  line={{ visible: true, width: 1, color: '#9CA3AF' }}
                />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem
                  title={{
                    text: yAxisTitle || undefined,
                    font: '12px sans-serif',
                    color: '#111827'
                  }}
                  min={0}
                  max={niceAxisMax}
                  labels={{ visible: true, color: '#374151' }}
                  majorGridLines={{ visible: true }}
                  line={{ visible: true, width: 1, color: '#9CA3AF' }}
                />
              </ChartValueAxis>
            </>
          ) : null}
          <ChartSeries>
            {type === 'line' ? (
              lineSeries.map((s) => (
                <ChartSeriesItem
                  key={s.name}
                  name={s.name}
                  type="line"
                  data={s.data}
                  field="value"
                  categoryField="category"
                  color={s.color}
                  markers={{ visible: true, size: 3 }}
                  tooltip={{
                    visible: true,
                    render: (ctx) => {
                      const c = ctx?.point?.category;
                      const v = ctx?.point?.value;
                      const dateStr = c instanceof Date
                        ? new Intl.DateTimeFormat(undefined, {
                            year: '2-digit', month: '2-digit', day: '2-digit',
                            hour: '2-digit', minute: '2-digit'
                          }).format(c)
                        : String(c);
                      return (
                        <span>
                          {xAxisTitle ? `${xAxisTitle}: ` : ''}{dateStr}
                          {yAxisTitle ? `, ${yAxisTitle}: ` : ': '}{v}
                        </span>
                      );
                    }
                  }}
                  labels={{ visible: false }}
                  overlay={{ gradient: 'none' }}
                />
              ))
            ) : (
              <ChartSeriesItem
                type={kendoType}
                data={points}
                field="value"
                categoryField="category"
                colorField="color"
                toggleable={false}
                tooltip={{
                  visible: true,
                  // For bar: show axis names, for pie just show category/value.
                  render: (ctx) => {
                    const c = ctx?.point?.category;
                    const v = ctx?.point?.value;
                    if (type === 'bar' && (xAxisTitle || yAxisTitle)) {
                      return (
                        <span>
                          {xAxisTitle ? `${xAxisTitle}: ` : ''}{c}
                          {yAxisTitle ? `, ${yAxisTitle}: ` : ': '}{v}
                        </span>
                      );
                    }
                    return <span>{c}: {v}</span>;
                  }
                }}
                labels={
                  type === 'pie'
                    ? {
                        visible: true,
                        position: 'outsideEnd',
                        content: pieLabelContent,
                        background: 'transparent',
                        color: '#111827',
                        font: '12px sans-serif',
                        // keep labels closer so they fit in tiles
                        distance: 6
                      }
                    : {
                        visible: true,
                        position: 'outsideEnd',
                        content: barLabelContent,
                        background: 'transparent',
                        color: '#111827',
                        font: '12px sans-serif'
                      }
                }
                padding={type === 'pie' ? 10 : undefined}
                // Keep pie a bit rotated like the demos
                startAngle={type === 'pie' ? 90 : undefined}
                overlay={{ gradient: 'none' }}
              />
            )}
          </ChartSeries>
        </Chart>
      </div>

      {type === 'pie' ? (
        <div
          style={{
            padding: '4px 8px 0',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            justifyContent: 'center',
            fontSize: 12,
            color: '#374151'
          }}
        >
          {points.map((p) => (
            <div
              key={p.category}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              title={p.category}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  background: p.color,
                  borderRadius: 2,
                  display: 'inline-block'
                }}
              />
              <span style={{ whiteSpace: 'nowrap' }}>{p.category}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default SimpleKendoChart;
